"use client";

import { create } from "zustand";
import { uiStorage } from "@/lib/storage";

export type ThemePref = "system" | "light" | "dark";
export type Density = "normal" | "compact";

export interface GridLayout {
  sizing?: Record<string, number>;
  order?: string[];
  hidden?: string[];
}

export interface SavedFilter {
  id: string;
  nome: string;
  valores: Record<string, string>;
}

interface PrefsState {
  theme: ThemePref;
  density: Density;
  grids: Record<string, GridLayout>;
  filters: Record<string, SavedFilter[]>;
  detailCollapsed: Record<string, boolean>;
  hydrate: () => void;
  setTheme: (t: ThemePref) => void;
  setDensity: (d: Density) => void;
  setGrid: (id: string, patch: GridLayout) => void;
  resetGrid: (id: string) => void;
  resetAllGrids: () => void;
  saveFilter: (screen: string, f: SavedFilter) => void;
  removeFilter: (screen: string, id: string) => void;
  setDetailCollapsed: (id: string, v: boolean) => void;
}

const KEY = "tms.prefs";

export function resolvedTheme(t: ThemePref): "light" | "dark" {
  if (t !== "system") return t;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function applyTheme(t: ThemePref, d: Density) {
  const el = document.documentElement;
  el.setAttribute("data-theme", resolvedTheme(t));
  el.setAttribute("data-density", d);
}

export const usePrefs = create<PrefsState>((set, get) => {
  const save = () => {
    const { theme, density, grids, filters, detailCollapsed } = get();
    uiStorage.set(KEY, { theme, density, grids, filters, detailCollapsed });
  };
  return {
    theme: "system",
    density: "normal",
    grids: {},
    filters: {},
    detailCollapsed: {},
    hydrate: () => {
      const saved = uiStorage.get<Partial<PrefsState>>(KEY, {});
      set({
        theme: saved.theme ?? "system",
        density: saved.density ?? "normal",
        grids: saved.grids ?? {},
        filters: saved.filters ?? {},
        detailCollapsed: saved.detailCollapsed ?? {},
      });
      applyTheme(get().theme, get().density);
    },
    setTheme: (theme) => {
      set({ theme });
      applyTheme(theme, get().density);
      save();
    },
    setDensity: (density) => {
      set({ density });
      applyTheme(get().theme, density);
      save();
    },
    setGrid: (id, patch) => {
      set({ grids: { ...get().grids, [id]: { ...get().grids[id], ...patch } } });
      save();
    },
    resetGrid: (id) => {
      const grids = { ...get().grids };
      delete grids[id];
      set({ grids });
      save();
    },
    resetAllGrids: () => {
      set({ grids: {} });
      save();
    },
    saveFilter: (screen, f) => {
      set({ filters: { ...get().filters, [screen]: [...(get().filters[screen] ?? []).filter((x) => x.nome !== f.nome), f] } });
      save();
    },
    removeFilter: (screen, id) => {
      set({ filters: { ...get().filters, [screen]: (get().filters[screen] ?? []).filter((x) => x.id !== id) } });
      save();
    },
    setDetailCollapsed: (id, v) => {
      set({ detailCollapsed: { ...get().detailCollapsed, [id]: v } });
      save();
    },
  };
});

/** Script inline que aplica o tema antes da primeira pintura. */
export const THEME_BOOT_SCRIPT = `(function(){try{var p=JSON.parse(localStorage.getItem("${KEY}")||"{}");var t=p.theme||"system";if(t==="system"){t=matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light"}document.documentElement.setAttribute("data-theme",t);document.documentElement.setAttribute("data-density",p.density||"normal")}catch(e){}})()`;
